import { combineQueries, createQuery, filterNilValue } from '@datorama/akita';
import { formatISO, max, min, parseISO } from 'date-fns';
import { flatMapDeep, isEqual, pick, uniq } from 'lodash-es';
import { distinctUntilChanged, map } from 'rxjs';
import userContextStore from 'context/UserContext/store';
import { entities$ as organisations$ } from 'state/Organisations/query';
import { type Organisation } from 'types/models';
import { isParentActive, type OrganisationParentValidity } from 'utils/organisationValidity';

export const organisationContextQuery = createQuery(userContextStore);
const periodContextQuery = createQuery(userContextStore);
const periodContext$ = periodContextQuery.select('activePeriod');

export const context$ = organisationContextQuery.select('activeOrganisation');

export const reducedContext$ = context$.pipe(
   map((org) => ({
      ...pick(org, [
         'id',
         'slug',
         'name',
         'code',
         'title',
         'comparisonPeriods',
         'favicon',
         'banner',
         'logo',
         'isWhiteLabel',
         'background',
         'theme',
         'allowMaterialityAssessment',
         'allowValidationRulesCheck',
         'allowDataEntry',
         'allowStrategyEntry',
         'allowClosingHub',
         'parentIds',
         'allowTaxonomyScoping',
      ]),
      label: org?.code ? `(${org.code}) ${org?.name}` : org?.name,
   })),
   distinctUntilChanged(isEqual)
);

export const activeOrganisationId$ = organisationContextQuery.select('activeOrganisationId');
export const organisationId$ = organisationContextQuery.select('organisationId');

export const reportingStandards$ = context$.pipe(
   map((org) => org?.reportingStandards ?? []),
   distinctUntilChanged(isEqual)
);

export const consolidationMethod$ = context$.pipe(
   map((org) => org?.consolidationMethod ?? 'FULL'),
   distinctUntilChanged(isEqual)
);

export const currencyCode$ = context$.pipe(
   map((org) => org?.currency?.shortCode),
   distinctUntilChanged()
);

export const currency$ = context$.pipe(
   map((org) => org?.currency),
   distinctUntilChanged()
);

export const type$ = context$.pipe(
   map((org) => org?.type ?? 'ORGANIZATION'),
   distinctUntilChanged()
);

export const subsidiaries$ = context$.pipe(
   map((org) => org?.subsidiaries ?? []),
   distinctUntilChanged(isEqual)
);

export const parents$ = context$.pipe(
   map((org) => org?.parents ?? []),
   distinctUntilChanged(isEqual)
);

export const minValidFrom$ = combineQueries([periodContext$, parents$]).pipe(
   map(([periodContext, parents]) => {
      if (!periodContext) {
         return undefined;
      }

      if (!parents || parents.length === 0) {
         return periodContext?.from;
      }

      const relevantParents = parents?.filter(({ validFrom }: OrganisationParentValidity) => !!validFrom);

      return relevantParents.length > 0
         ? formatISO(min(relevantParents.map(({ validFrom }: OrganisationParentValidity) => parseISO(validFrom!))), { representation: 'date' })
         : periodContext?.from;
   }),
   distinctUntilChanged()
);

export const maxValidTo$ = combineQueries([periodContext$, parents$]).pipe(
   map(([periodContext, parents]) => {
      if (!periodContext) {
         return undefined;
      }

      if (!parents || parents.length === 0) {
         return periodContext?.to;
      }

      const relevantParents = parents?.filter(({ validTo }: OrganisationParentValidity) => !!validTo);

      return relevantParents.length > 0
         ? formatISO(max(relevantParents.map(({ validTo }: OrganisationParentValidity) => parseISO(validTo!))), { representation: 'date' })
         : periodContext?.to;
   }),
   distinctUntilChanged()
);

export const allowDataEntry$ = combineQueries([reducedContext$, parents$, periodContext$]).pipe(
   map(
      ([organisationContext, parents, periodContext]) =>
         organisationContext?.allowDataEntry === true &&
         periodContext &&
         ((parents ?? [])?.length === 0 ||
            (parents ?? []).some((parent: OrganisationParentValidity) => isParentActive(parent, periodContext?.from, periodContext?.to)))
   ),
   distinctUntilChanged()
);

export const characteristics$ = context$.pipe(
   map((org) => org?.characteristics ?? []),
   distinctUntilChanged(isEqual)
);

export const results$ = context$.pipe(
   map((org) => org?.results ?? []),
   distinctUntilChanged(isEqual)
);

export const subsidiaryIds$ = context$.pipe(
   filterNilValue(),
   map((activeOrganisation) => flatMapDeep([activeOrganisation], flattenSubsidiaries).map((org) => org?.id)),
   distinctUntilChanged(isEqual)
);

export const topLevelOrganisationId$ = organisations$.pipe(
   map((organisations) => organisations.find((org) => !org?.parents || (Array.isArray(org?.parents) && org.parents.length === 0))?.id),
   distinctUntilChanged()
);

export const isOrganisationActive$ = combineQueries([activeOrganisationId$, topLevelOrganisationId$, periodContext$, parents$]).pipe(
   map(
      ([activeOrganisationId, topLevelOrganisationId, periodContext, parents]) =>
         activeOrganisationId === topLevelOrganisationId ||
         (periodContext &&
            (parents ?? []).some((parent: OrganisationParentValidity) => isParentActive(parent, periodContext?.from, periodContext?.to)))
   )
);

export const parentIds$ = combineQueries([activeOrganisationId$, organisations$, reducedContext$]).pipe(
   map(([activeOrganisationId, organisations, context]) => {
      if (Number.isInteger(activeOrganisationId) && Array.isArray(organisations) && organisations.length > 0) {
         return uniq([...(context?.parentIds ?? []), ...getParentIds(activeOrganisationId!, organisations)]);
      }

      return [];
   }),
   distinctUntilChanged(isEqual)
);

export const hasParents$ = parents$.pipe(
   map((parents) => parents?.length > 0),
   distinctUntilChanged()
);

export const customization$ = context$.pipe(
   map((org) => org?.customization),
   distinctUntilChanged(isEqual)
);

export const theme$ = context$.pipe(
   map((org) => org?.theme),
   distinctUntilChanged(isEqual)
);

export function getParentIds(currentOrganisationId: number, organisations: Organisation[], first = true): number[] {
   const currentOrganisation = organisations.find(({ id }) => id === currentOrganisationId);

   if (!Array.isArray(currentOrganisation?.parents)) return [];

   if (currentOrganisation?.parents.length === 0) {
      return first ? [] : [currentOrganisationId];
   }

   return (currentOrganisation?.parents ?? []).reduce(
      (parentIds, parent) => uniq([...parentIds, parent.id, ...getParentIds(parent.id, organisations, false)]),
      [] as number[]
   );
}

export function getSubsidiaryIds(currentOrganisationId: number, organisations: Organisation[]): number[] {
   const subsidiaries = organisations.filter(({ parents }) => (parents ?? []).some(({ id }) => id === currentOrganisationId));

   return uniq([...subsidiaries.map(({ id }) => id), ...subsidiaries.flatMap(({ id }) => getSubsidiaryIds(id, organisations))]);
}

export function flattenSubsidiaries(item: Organisation): Organisation[] {
   return [item, ...flatMapDeep(item.subsidiaries, flattenSubsidiaries)];
}
